import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Form, Input, Select, DatePicker, Checkbox, Table, Row, Col, Space, Tag, message } from 'antd';
import withAntDPagination from "../../common/higherOrderComponents/withAntDPagination";
import axios from "../../common/axios";
import SyllabusTopicConceptSelect from "../../common/SyllabusTopicConceptSelect";
import {UserContext} from "../../common/UserContext";
import {BrandContext} from "../../common/BrandContext";
import PopConfirm from "../../common/PopConfirm";
import ClassesMultiSelect from "../../common/ClassesMultiSelect";
import EditTrailAssignmentDialog from "./EditTrailAssignmentDialog";
import TableFilterControls from "../../common/TableFilterControls";
import TableFilterControlsForm from "../../common/TableFilterControlsForm";
import TableFilterControlsSection from "../../common/TableFilterControlsSection";
import Button from "../../common/Button";
import TableAntD from "../../common/TableAntD";
import {useFormFilterBadge} from "../../hooks/useFormFilterBadge";
import TrailAssignmentProgress from "./TrailAssignmentProgress";

const { Option } = Select;
const { RangePicker } = DatePicker;

const TeacherTrailAssignments = (props) => {
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const {sharedPathways = false, schoolPathways = false} = props;
    const [pathwaySearchForm] = Form.useForm();
    const [pathwayType, setPathwayType] = useState('ALL');
    const [pathwaySearchType, setPathwaySearchType] = useState('OWN');
    const [archived, setArchived] = useState(false);
    const [classIds, setClassIds] = useState([]);
    const [selectedAssignments, setSelectedAssignments] = useState(() => new Map());
    const [isDisableClose, setDisableClose] = useState(true);
    const [isDisableArchive, setDisableArchive] = useState(true);
    const [trailAssignments, setTrailAssignments] = useState([]);
    const [trailAssignmentsCount, setTrailAssignmentsCount] = useState(0);
    const {activeFilterCount, onLoadComplete, handleValuesChange: handleBadgeValuesChange} = useFormFilterBadge(pathwaySearchForm);
    const [isEditTrailAssignmentDialogOpen, setEditTrailAssignmentDialogOpen] = useState(false);
    const [trailAssignment, setTrailAssignment] = useState(null);
    const [reload, setReload] = useState();
    const [trailAssignmentId, setTrailAssignmentId] = useState(null);

    globalReactFunctions.refreshTeacherTrailAssignments = () => {
        setReload(new Date().getTime());
        clearSelectedAssignments();
    };
    globalReactFunctions.setTrailAssignmentId = trailAssignmentId => {
        setTrailAssignmentId(trailAssignmentId);
    };

    const addSelectedAssignment = (key, value) => {
        setSelectedAssignments(prevMap => {
            const newMap = new Map(prevMap);
            newMap.set(key, value);
            return newMap;
        });
    };

    const addAllSelectedAssignments = (trailAssignments) => {
        setSelectedAssignments(prevMap => {
            const newMap = new Map(prevMap);
            trailAssignments.forEach(trail => {
                newMap.set(trail.pathwayId, trail);
            });
            return newMap;
        });
    };

    const removeSelectedAssignment = (key) => {
        setSelectedAssignments(prevMap => {
            const newMap = new Map(prevMap);
            newMap.delete(key);
            return newMap;
        });
    };

    const clearSelectedAssignments  = () => {
        setSelectedAssignments(new Map());
    };

    useEffect(() => {
        let disableClose = true;
        let disableArchive = true;
        for(const assignment of selectedAssignments.values()) {
            const owned = assignment.teacherId === user.userId;

            if(assignment.open && owned) {
                disableClose = false;
            }
            if(assignment.closed && owned) {
                disableArchive = false;
            }
        }

        setDisableClose(disableClose);
        setDisableArchive(disableArchive);
    }, [selectedAssignments]);

    const handleEditTrailAssignment = record => {
        setEditTrailAssignmentDialogOpen(true);
        setTrailAssignment(record);
    }

    const columns = [
        {
            title: <Checkbox
                aria-label="select all task assignments"
                checked={trailAssignments.length > 0 && selectedAssignments.size === trailAssignments.length}
                onChange={(e) => onSelectAllRows(e.target.checked)}
            ><span className="ch-accessibility-label">select task all assignments</span></Checkbox>,
            dataIndex: 'checkbox',
            width: 50,
            render: (_, record) => (
                <Checkbox
                    aria-label="select task assignment"
                    checked={selectedAssignments.has(record.pathwayAssignmentId)}
                    onChange={e => onSelectRow(e.target.checked, record)}
                ><span className="ch-accessibility-label">select task assignment</span></Checkbox>
            ),
        },
        {
            title: brandConfig.taskName + ' name',
            dataIndex: 'pathwayAssignmentName',
            sorter: false,
            render: (_, record) => <><span>{record.pathwayAssignmentName}</span> {record.pathwayType == 'PATHWAY' ?  <Tag className="ch-tag-secondary">Template</Tag> :<></>} {record.message && <span className="ch-text-note"><br/>{record.message}</span>}</>
        },
        {
            title: 'Recipients',
            dataIndex: 'recipientGroup',
            sorter: false,
            render: (_, record) => record.recipientGroup === 'CLASSES' ? record.classes.map((recipientClass, index) => <div key={recipientClass.classId}><Button type="text" onClick={() => {handleViewPathwayAssignmentRecipients(record.pathwayAssignmentId, recipientClass.classId)}}>{recipientClass.className}</Button>{index < record.classes.length - 1 && <br/>}</div>) :  <Button type="link" onClick={() => {handleViewPathwayAssignmentRecipients(record.pathwayAssignmentId)}}>{record.studentNames.slice(0, 13)}...</Button>
        },
        {
            title: 'Status',
            dataIndex: 'pathwayAssignmentState',
            align: 'center',
            render: (_, record) => record.pathwayAssignmentStateDescription
        },
        {
            title: 'Assigned',
            dataIndex: 'assignedDate',
            sorter: false,
            render: (value, record) => {
                if(record.assignedDate === undefined) {
                    return '';
                }

                const dateFormatted = user.dateTimeFormat.format(record.assignedDate);
                return <span>{dateFormatted}<br/>{record.teacherFirstName} {record.teacherLastName}</span>;
            }
        },
        {
            title: 'Started',
            dataIndex: 'startDate',
            sorter: false,
            render: (_, record) => {
                if(record.firstUseDate === undefined) {
                    return (<Button type="primary" onClick={() => handleEditTrailAssignment(record)}>Edit</Button>);
                }

                const dateFormatted = user.dateTimeFormat.format(record.firstUseDate);
                return dateFormatted;
            }
        },
        {
            title: 'Action',
            dataIndex: 'action',
            render: (_, record) => <span><Button type="primary" onClick={() => handleViewProgress(record.pathwayAssignmentId)}>Progress</Button>{record.firstUseDate !== undefined && <><br/><span style={{whiteSpace: 'nowrap'}}>{record.percentStudentsComplete}%  completed</span></>}</span>
        }
    ];

    const onSelectRow = (checked, pathwayAssignment) => {
        if(checked) {
            addSelectedAssignment(pathwayAssignment.pathwayAssignmentId, pathwayAssignment);
        } else {
            removeSelectedAssignment(pathwayAssignment.pathwayAssignmentId);
        }
    };

    const onSelectAllRows = (checked) => {
        if (checked) {
            addAllSelectedAssignments(trailAssignments);
        } else {
            clearSelectedAssignments();
        }
    };

    const handleValuesChange = (changedValues, allValues) => {
        const keys = Object.keys(changedValues);
        const hasOtherKeys = keys.some(key => key !== 'pathwayAssignmentName' && key !== 'dateRange' && key !== 'studentName');

        if (hasOtherKeys) {
            handleBadgeValuesChange();
        }

        if ('syllabusId' in changedValues || 'syllabusGroupId' in changedValues) {
            handleSearch();
        }
    }

    const handleSearch = () => {
        handleBadgeValuesChange();
        setReload(new Date().getTime());
    };

    const handleChangePathwayType = (value) => {
        setPathwayType(value);
    };

    const handleChangePathwaySearchType = (value) => {
        setPathwaySearchType(value);
    };

    const handleClose = () => {
        if (selectedAssignments && selectedAssignments.size > 0) {
            openClosePathwayAssignmentDialog(Array.from(selectedAssignments.keys()));
        }  else {
            message.error('Please select a single pathway to close.');
        }
    };

    const handleArchive = () => {
        if (selectedAssignments && selectedAssignments.size > 0) {
            openArchivePathwayAssignmentDialog(Array.from(selectedAssignments.keys()))
        } else {
            message.error('Please select a single pathway to archive.');
        }
    };

    const handleViewPathwayAssignmentRecipients = (pathwayAssignmentId, classId) => {
        console.log('handleViewPathwayAssignmentRecipients', pathwayAssignmentId, classId);
        if(!pathwayAssignmentId) {
            return;
        }

        openPathwayAssignmentRecipientsDialog(pathwayAssignmentId, classId);
    };

    const handleViewProgress = (pathwayAssignmentId) => {
        loadPathwayTracker(pathwayAssignmentId);
    };

    const handleCloseSearchTrailAssignments = () => {
        const formValues = pathwaySearchForm.getFieldsValue();
        const pathwayAssignmentName = formValues.pathwayAssignmentName || undefined;
        const topicId = formValues.topicId || undefined;
        const conceptId = formValues.conceptId || undefined;
        const [startDate, endDate] = formValues.dateRange || [];

        const payload = {
            pathwayType: pathwayType == 'ALL' ? undefined : pathwayType,
            pathwaySearchType: pathwaySearchType,
            classIds: classIds,
            schoolPathways: schoolPathways,
            sharedPathways: sharedPathways,
            pathwayAssignmentName: pathwayAssignmentName,
            archived: archived,
            topicId: topicId,
            conceptId: conceptId,
            startDate: startDate,
            endDate: endDate
        };
        axios.post('/web/ui/trail/trailAssignments/search/close', payload,  {
            headers: {
                'Content-Type': 'application/json'
            }
        }).then((response) => {
            setTrailAssignmentsCount(0);
            setReload(new Date().getTime());
        });
    };

    const handleArchiveSearchTrailAssignments = () => {
        const formValues = pathwaySearchForm.getFieldsValue();
        const pathwayAssignmentName = formValues.pathwayAssignmentName || undefined;
        const topicId = formValues.topicId || undefined;
        const conceptId = formValues.conceptId || undefined;
        const [startDate, endDate] = formValues.dateRange || [];

        const payload = {
            pathwayType: pathwayType == 'ALL' ? undefined : pathwayType,
            pathwaySearchType: pathwaySearchType,
            classIds: classIds,
            schoolPathways: schoolPathways,
            sharedPathways: sharedPathways,
            pathwayAssignmentName: pathwayAssignmentName,
            archived: archived,
            topicId: topicId,
            conceptId: conceptId,
            startDate: startDate,
            endDate: endDate
        };
        axios.post('/web/ui/trail/trailAssignments/search/archive', payload,  {
            headers: {
                'Content-Type': 'application/json'
            }
        }).then((response) => {
            setTrailAssignmentsCount(0);
            setReload(new Date().getTime());
        });
    };


    const handleConfirmCloseSchoolPathwayAssignments = (open) => {
        if(open) {
            axios.get('/web/ui/trail/trailAssignments/school/count', {params: {open: true}}).then((response) => {
                setTrailAssignmentsCount(response.data);
            }).catch((error) => {
                console.error(error);
                setTrailAssignmentsCount(0);
            })
        }
    };

    const handleCloseSchoolPathwayAssignments = () => {
        axios.post('/web/ui/trail/trailAssignments/school/close').then((response) => {
            setTrailAssignmentsCount(0);
            setReload(new Date().getTime());
        });;
    };

    const handleConfirmArchiveSchoolPathwayAssignments = (open) => {
        if(open) {
            axios.get('/web/ui/trail/trailAssignments/school/count', {params: {open: false}}).then((response) => {
                setTrailAssignmentsCount(response.data);
            }).catch((error) => {
                console.error(error);
                setTrailAssignmentsCount(0);
            })
        }
    };

    const handleArchiveSchoolPathwayAssignment = () => {
        axios.post('/web/ui/trail/trailAssignments/school/archive').then((response) => {
            setTrailAssignmentsCount(0);
            setReload(new Date().getTime());
        });
    };

    const fetchData = (pagingParams, handleTableData, handlePagingData, abortController) => {
        const formValues = pathwaySearchForm.getFieldsValue();
        const pathwayAssignmentName = formValues.pathwayAssignmentName || undefined;
        const topicId = formValues.topicId || undefined;
        const conceptId = formValues.conceptId || undefined;
        const [startDate, endDate] = formValues.dateRange || [];

        const payload = {
            pageNumber:  pagingParams.pageNumber,
            pageSize: pagingParams.pageSize,
            pathwayGroupCode: undefined,
            pathwayType: pathwayType == 'ALL' ? undefined : pathwayType,
            pathwaySearchType: pathwaySearchType,
            classIds: classIds,
            schoolPathways: schoolPathways,
            sharedPathways: sharedPathways,
            pathwayAssignmentName: pathwayAssignmentName,
            archived: archived,
            topicId: topicId,
            conceptId: conceptId,
            startDate: startDate,
            endDate: endDate
        };

        axios.post('/web/ui/trail/trailAssignments/search', payload, {
            headers: {
                'Content-Type': 'application/json'
            },
            signal: abortController.signal
        }).then(response => {
            handleTableData(response.data.currPageContents);
            setTrailAssignments(response.data.currPageContents);
            setTrailAssignmentsCount(response.data.totalResults);
            handlePagingData({
                pageSize: pagingParams.pageSize,
                totalResults: response.data.totalResults ?? 0,
            });
        })
        .catch(error => {
            console.error('Error loading data:', error);
            handleTableData([]);
        });
    };

    const TrailAssignmentsTable = useMemo(() => {
        return withAntDPagination(Table, fetchData, {
            pageSize: 10,
            currentPage: 1,
        });
    }, [reload, pathwayType, archived, classIds, pathwaySearchType]);

    const getTableButtons = () => {
        return(
            <Row justify="start" className="margin-t--m margin-b--m">
                <Space>
                    <Button type="primary" onClick={handleClose} disabled={selectedAssignments.size == 0 || isDisableClose}>Close</Button>
                    <Button type="outline" onClick={handleArchive} disabled={selectedAssignments.size == 0 || isDisableArchive}>Archive</Button>
                    <PopConfirm
                        title={'Close All Search ' + brandConfig.tasksName + ' assignments'}
                        description={<span>Are you sure you want to close all <strong>{trailAssignmentsCount}</strong> {brandConfig.tasksName.toLowerCase()}  assignments in your search?</span>}
                        onConfirm={handleCloseSearchTrailAssignments}
                    >
                        <Button type="outline" disabled={trailAssignments === undefined || trailAssignments.length == 0}>Close Search Results</Button>
                    </PopConfirm>
                    <PopConfirm
                        title={'Archive Search ' + brandConfig.tasksName + ' assignments'}
                        description={<span>Are you sure you want to archive all <strong>{trailAssignmentsCount}</strong> {brandConfig.tasksName.toLowerCase()}  assignments in your search?</span>}
                        onConfirm={handleArchiveSearchTrailAssignments}
                    >
                        <Button type="outline" disabled={trailAssignments === undefined || trailAssignments.length == 0}>Archive Search Results</Button>
                    </PopConfirm>
                    <PopConfirm
                        title={'Close All School' + brandConfig.tasksName + ' assignments'}
                        description={<span>Are you sure you want to close all <strong>{trailAssignmentsCount}</strong> {brandConfig.tasksName.toLowerCase()}  assignments in your school?</span>}
                        onOpenChange={handleConfirmCloseSchoolPathwayAssignments}
                        onConfirm={handleCloseSchoolPathwayAssignments}
                    >
                        <Button type="outline">Close All School</Button>
                    </PopConfirm>
                    <PopConfirm
                        title={'Archive All School' + brandConfig.tasksName + ' assignments'}
                        description={<span>Are you sure you want to archive all <strong>{trailAssignmentsCount}</strong> {brandConfig.tasksName.toLowerCase()}  assignments in your school?</span>}
                        onOpenChange={handleConfirmArchiveSchoolPathwayAssignments}
                        onConfirm={handleArchiveSchoolPathwayAssignment}
                    >
                        <Button type="outline">Archive All School</Button>
                    </PopConfirm>
                </Space>
            </Row>
        )
    }

    if (trailAssignmentId != null) {
        return (
            <div>
                <TrailAssignmentProgress trailAssignmentId={trailAssignmentId} onExit={() => setTrailAssignmentId(null)} />
            </div>
        )
    }

    return (
        <div>
            <div>
                <div>
                    <TableFilterControls
                        filterCount={activeFilterCount}
                        showFilterButton={true}
                        primaryFilterItem={
                            <Form.Item label="Show: ">
                                <Space>
                                    <Select
                                        aria-label="task type"
                                        value={pathwayType}
                                        onChange={handleChangePathwayType}
                                        style={{ width: 180 }}
                                    >
                                        <Option value="ALL">Tasks and templates</Option>
                                        <Option value="TASK">Tasks</Option>
                                        <Option value="PATHWAY">Templates</Option>
                                    </Select>
                                </Space>
                            </Form.Item>
                        }
                        filterContent={
                            <TableFilterControlsForm
                                form={pathwaySearchForm}
                                onFinish={handleSearch}
                                onValuesChange={handleValuesChange}
                                initialValues={{ nameSearch: '' }}
                                useApplyButton={true}
                            >
                                <TableFilterControlsSection>
                                    <Form.Item label="Task name" name="pathwayAssignmentName">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item label="Classes" name="classIds">
                                        <ClassesMultiSelect
                                            classes={user.classes}
                                            selectedClassIds={classIds}
                                            onChange={(ids) => setClassIds(ids)}
                                            width={'100%'}
                                        />
                                    </Form.Item>
                                    <Form.Item label="Assigned date" name="dateRange">
                                        <RangePicker format={user.getDateFormatString()} style={{width: '100%'}} />
                                    </Form.Item>
                                </TableFilterControlsSection>
                                <TableFilterControlsSection>
                                    <Form.Item label="Recipients">
                                        <Select
                                            aria-label="recipients"
                                            value={pathwaySearchType}
                                            onChange={handleChangePathwaySearchType}
                                        >
                                            <Option value="OWN">
                                                My {brandConfig.tasksName.toLowerCase()}
                                            </Option>
                                            <Option value="SCHOOL">
                                                School {brandConfig.tasksName.toLowerCase()}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item name="contentCategory" label="Content category">
                                        <Select
                                            mode="multiple"
                                            allowClear={true}
                                            value={archived ? ['archived'] : []}
                                            onChange={(values) => {
                                                setArchived(values.includes('archived'));
                                            }}
                                            placeholder="Select content category"
                                        >
                                            <Option value="archived">Archived only</Option>
                                        </Select>
                                    </Form.Item>
                                </TableFilterControlsSection>
                                <TableFilterControlsSection showDivider={false}>
                                    <SyllabusTopicConceptSelect
                                        form={pathwaySearchForm}
                                        selectLevel="concept"
                                        defaultLevel="syllabus"
                                        onLoadComplete={onLoadComplete}
                                    />
                                </TableFilterControlsSection>
                            </TableFilterControlsForm>
                        } />
                </div>
                {getTableButtons()}
                <TableAntD
                    dynamicWidth={true}
                    pagingData={{ fetchFunction: fetchData, params: { pageSize: 10, currentPage: 1}}}
                    pagingDependencies={[reload, pathwayType, archived, classIds, pathwaySearchType]}
                    columnData={columns}
                    rowKey="pathwayAssignmentId"
                    rowClassName="ch-align-top"
                    locale={{
                        emptyText: (
                            <span className="ch-warning-text">No {brandConfig.taskName} assignments found</span>
                        )
                    }}
                />
                {getTableButtons()}
                <EditTrailAssignmentDialog
                    open={isEditTrailAssignmentDialogOpen}
                    onClose={() => setEditTrailAssignmentDialogOpen(false)}
                    setReload={handleSearch}
                    trailAssignment={trailAssignment}
                />
            </div>
        </div>
    );
};

export default TeacherTrailAssignments;